import { stegaClean, toPlainText } from 'next-sanity'

import type { PortableTextBlock } from 'next-sanity'

import { type SectionStyle, sectionStyleClasses } from '@/app/components/PageBuilder/ContentSection'
import PortableText from '@/app/components/PortableText'
import { Review } from '@/app/components/Review'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { getBottomSpacingClass, getTopSpacingClass } from '@/app/utils/sectionSpacing'
import { cn } from '@/app/utils/style'
import type { ReviewSection as ReviewSectionProps } from '@/sanity.types'
import { dataAttr } from '@/sanity/lib/utils'

type ReviewSectionComponentProps = {
  block: ReviewSectionProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
  isLast?: boolean
}

export default function ReviewSection({
  block,
  index,
  pageId,
  pageType,
}: ReviewSectionComponentProps) {
  if (!block?.review || !block?.contentBlock?.content) return null

  const style = stegaClean(block.style) || 'default'
  const reviewPosition = stegaClean(block.reviewPosition) || 'left'
  const contentDesktopAlignment = stegaClean(block.contentDesktopAlignment) || 'start'
  const topSpacingClass = getTopSpacingClass(stegaClean(block.topSpacing))
  const bottomSpacingClass = getBottomSpacingClass(stegaClean(block.bottomSpacing))

  const baseStyleConfig = sectionStyleClasses[style as SectionStyle] || sectionStyleClasses.default

  // ReviewSection uses same container/inner classes but adds text-white-default to green container
  const styleConfig = {
    ...baseStyleConfig,
    container:
      style === 'green'
        ? `${baseStyleConfig.container} text-white-default`
        : baseStyleConfig.container,
  }

  const rawAttr =
    block._key && pageId && pageType
      ? dataAttr({
          id: pageId,
          type: pageType,
          path: `pageBuilder[_key=="${block._key}"]`,
        })
      : undefined

  // Filter out non-DOM properties (like toString) before spreading
  const attr = rawAttr
    ? Object.fromEntries(Object.entries(rawAttr).filter(([key]) => key.startsWith('data-')))
    : {}

  return (
    <div className={cn(styleConfig.container)} {...attr}>
      <div
        className={cn(
          'mx-auto max-w-[1144px]',
          styleConfig.inner,
          topSpacingClass,
          bottomSpacingClass,
        )}
      >
        {/* Two-column layout matching the contentSection + group structure */}
        <div
          className={cn(
            'flex flex-col items-center justify-center gap-9xl',
            reviewPosition === 'stacked'
              ? 'flex-col-reverse lg:justify-center lg:items-center'
              : 'lg:flex-row lg:justify-start',
            reviewPosition === 'right' && 'flex-col-reverse lg:flex-row-reverse',
            contentDesktopAlignment === 'center' && reviewPosition !== 'stacked'
              ? 'lg:items-center'
              : 'lg:items-start',
          )}
        >
          {/* Review column */}
          <div
            className={cn(
              'flex items-center justify-center lg:flex-1',
              reviewPosition === 'stacked' && 'max-w-[600px] self-center',
            )}
          >
            <Review value={block.review} tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }} />
          </div>

          {/* Content block column */}
          {toPlainText(block.contentBlock.content) && (
            <div
              className={cn(
                'flex flex-col items-center justify-center gap-lg lg:flex-1',
                reviewPosition === 'stacked' ? 'self-center' : 'lg:items-start lg:justify-start',
              )}
            >
              <PortableText
                className={cn(styleConfig.portableText)}
                value={block.contentBlock.content as PortableTextBlock[]}
                tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                documentId={pageId}
                documentType={pageType}
                parentPath={
                  block._key ? `pageBuilder[_key=="${block._key}"].contentBlock.content` : undefined
                }
                headingsAsParagraphs
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
